/**
 * Generates a working Geo-Lift/Synthetic Control sample CSV, built entirely
 * from MIN_GEO_LIFT_LOCATIONS/MIN_GEO_LIFT_TIME_PERIODS so it always
 * satisfies the same rules enforced by the upload form and backend
 * /validateData - if those minimums ever change, this template changes with
 * them instead of silently going stale.
 *
 * One extra location beyond the minimum is included and kept at zero spend
 * for the whole period, demonstrating the required control market; the rest
 * get spend only in the final few weeks, demonstrating a real campaign
 * window.
 */
import { MIN_GEO_LIFT_LOCATIONS, MIN_GEO_LIFT_TIME_PERIODS } from "../constants/common";

const SAMPLE_STATE_NAMES = [
  "California",
  "Texas",
  "New York",
  "Florida",
  "Illinois",
  "Georgia",
  "Ohio",
  "Washington",
];

const CAMPAIGN_WEEKS = 4;

function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function buildGeoLiftSampleCsv(): string {
  const totalLocations = Math.min(SAMPLE_STATE_NAMES.length, MIN_GEO_LIFT_LOCATIONS + 1);
  const locations = SAMPLE_STATE_NAMES.slice(0, totalLocations);
  // Last location is the control market: zero spend for the whole period.
  const controlLocation = locations[locations.length - 1];

  const totalWeeks = MIN_GEO_LIFT_TIME_PERIODS + 4; // comfortably above the minimum
  const startDate = new Date("2025-01-06T00:00:00Z"); // a Monday

  const rows: string[] = ["location,date,Y,adspend"];

  locations.forEach((location, locationIndex) => {
    for (let week = 0; week < totalWeeks; week++) {
      const date = new Date(startDate);
      date.setUTCDate(date.getUTCDate() + week * 7);

      const baseline = 10000 + locationIndex * 1500;
      const weeklyVariation = Math.sin(week / 3 + locationIndex) * 400;
      const y = (baseline + weeklyVariation).toFixed(2);

      const isInCampaignWindow = week >= totalWeeks - CAMPAIGN_WEEKS;
      const isControl = location === controlLocation;
      const adspend = isInCampaignWindow && !isControl ? (2000 + locationIndex * 250).toFixed(2) : "0";

      rows.push(`"${location}",${formatDate(date)},${y},${adspend}`);
    }
  });

  return rows.join("\n");
}

export function downloadGeoLiftSampleTemplate(): void {
  const csv = buildGeoLiftSampleCsv();
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = "geo-lift-sample-template.csv";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
